import React, { forwardRef } from 'react';
import PropTypes from 'prop-types';
import { cx } from './classNames';

// MUI's small status Chip in the app theme (MuiChip and its semantic colour
// variants in styles/msiMuiComponentOverrides.js, at the size the optimizer
// status chips set): a 24px pill (22px compact) with a 1px tone border, tone
// fill and tone text, an optional 14px leading icon and a 10.5px semibold
// uppercase label. Neutral is MUI's default chip: dark line border, subtle
// surface, muted ink.
const CHIP_CLASS_NAME = cx(
    'box-border inline-flex h-6 max-w-full shrink-0 items-center whitespace-nowrap rounded-full border',
    'border-solid align-middle text-[0.66rem] font-semibold uppercase leading-none tracking-normal',
    '[[data-compact]_&]:h-[22px]',
);
const TONE_CLASS_NAMES = Object.freeze({
    success: 'border-ui-success-line bg-ui-success-bg text-ui-success',
    warning: 'border-ui-warning-line bg-ui-warning-bg text-ui-warning',
    danger: 'border-ui-danger-line bg-ui-danger-bg text-ui-danger',
    info: 'border-ui-info-line bg-ui-info-bg text-ui-info',
    neutral: 'border-ui-line-strong bg-tbl-stripe text-ui-muted',
});

/**
 * A non-interactive status label. `tone` picks the colours (also exposed as
 * `data-tone`); `icon` is an optional leading icon element (sized to 14px).
 * Takes a ref and passes other props to its <span>, so it can anchor a
 * Tooltip.
 */
const Chip = forwardRef(function Chip({
    label,
    icon = null,
    tone = 'neutral',
    className,
    ...spanProps
}, ref) {
    return (
        <span
            ref={ref}
            data-tone={tone}
            className={cx(CHIP_CLASS_NAME, TONE_CLASS_NAMES[tone], className)}
            {...spanProps}
        >
            {icon && (
                <span aria-hidden="true" className="-mr-1 ml-1 inline-flex shrink-0 [&>svg]:h-3.5 [&>svg]:w-3.5">
                    {icon}
                </span>
            )}
            <span className="min-w-0 overflow-hidden text-ellipsis px-1.5">{label}</span>
        </span>
    );
});

Chip.propTypes = {
    label: PropTypes.node.isRequired,
    icon: PropTypes.node,
    tone: PropTypes.oneOf(['success', 'warning', 'danger', 'info', 'neutral']),
    className: PropTypes.string,
};

export default Chip;
